<template>
  <div class="v-address-editor" :class="{ ['active']: isActive }">
    <div v-if="!isActive" class="placeholder-wrapper" @click="toggleActive">
      <div class="icon">
        <img :src="addressString ? checkIconActive : checkIcon" />
      </div>
      <div class="title">Address</div>
      <div class="value">
        <span class="address-string">{{ addressString }}</span>
      </div>
    </div>

    <div v-if="isActive" class="edit-area-wrapper">
      <div class="address-title">
        <div class="icon">
          <img :src="_isRequiredFieldsFilled ? checkIconActive : checkIcon" />
        </div>
        <div
          class="title"
          :class="{ ['checked']: this._isRequiredFieldsFilled }"
        >
          Address
        </div>
      </div>

      <VEditableText
        _key="street"
        label="Address Line 1"
        :value="address.street"
        addPlaceholder="e.g. 1 Rocket Road"
        @change="updateField"
        :validation="[required]"
        extraClass="address-line"
      />

      <VEditableText
        _key="unit"
        label="Address Line 2"
        :value="address.unit"
        addPlaceholder="e.g. Suite 1"
        @change="updateField"
        extraClass="address-line"
      />

      <VEditableText
        _key="city"
        label="City"
        addPlaceholder="e.g. Hawthorne"
        :value="address.city"
        :validation="[required]"
        @change="updateField"
      />

      <div class="placeholder-wrapper" @click="statesDpOpened = true">
        <div class="icon">
          <img :src="address.state ? checkIconActive : checkIcon" />
        </div>
        <div class="title">State</div>
        <div
          v-if="!statesDpOpened"
          class="value"
          :class="{ ['no-state']: !!address.state }"
        >
          <span>{{ address.state || "Add" }}</span>
        </div>

        <VDropDownSelect
          :items="_convertListToSelectFormat('states')"
          :selected-items="[address.state]"
          :initialInput="address.state"
          @select="val => updateField('state', val)"
          disableCreate
          v-else
        />
      </div>

      <VEditableText
        _key="zip"
        label="ZIP"
        :value="address.zip"
        addPlaceholder="e.g. 90250"
        @change="updateField"
        :validation="[required]"
      />

      <div class="placeholder-wrapper" @click="countriesDpOpened = true">
        <div class="icon">
          <img :src="address.country ? checkIconActive : checkIcon" />
        </div>
        <div class="title">Country</div>
        <div class="value" v-if="!countriesDpOpened">
          <span>{{ address.country }}</span>
        </div>

        <VDropDownSelect
          v-else
          :items="_convertListToSelectFormat('countries')"
          :selected-items="[address.country]"
          :initialInput="address.country"
          @select="val => updateField('country', val)"
          disableCreate
        />
      </div>
    </div>

    <div v-if="isActive" class="v-address-editor__buttons-wrapper">
      <button @click="_cancel">Cancel</button>
      <button
        @click="_save"
        class="save"
        :class="{ ['disabled']: !this.isFieldsValid }"
        :disabled="!isFieldsValid"
      >
        Save
      </button>
    </div>
  </div>
</template>

<script>
import Vue from "vue";
import VueTypes from "vue-types";
import sortBy from "lodash/sortBy";

// components
import VEditableText from "@/components/VEditableText";
import VDropDownSelect from "@/components/VDropDownSelect";

// mixins
import withToasted from "@/mixins/withToasted";

//validation
import { required, zip } from "@/utils/validations";

// assets
import checkIcon from "@/assets/images/checkmark.svg";
import checkIconActive from "@/assets/images/checkmark-checked.svg";

import { COUNTRIES_SELECT_LIST, STATES_SELECT_LIST } from "@/constants/select";

export default Vue.component("VAddressEditor", {
  props: {
    initialValues: VueTypes.object.def({})
  },

  components: {
    VEditableText,
    VDropDownSelect
  },

  mixins: [withToasted],

  data() {
    return {
      isActive: false,
      countriesDpOpened: false,
      statesDpOpened: false,
      checkIcon,
      checkIconActive,
      COUNTRIES_SELECT_LIST,
      STATES_SELECT_LIST,
      //validators
      required,
      zip,
      address: {
        ...this.initialValues
      }
    };
  },

  computed: {
    _isRequiredFieldsFilled() {
      return ["street", "country", "state", "zip", "city"].every(
        field => this.address[field] && this.address[field] !== ""
      );
    },

    isFieldsPassedValidation() {
      return !zip(this.address.zip);
      // &&
      // this.validateState(this.address.state, {
      //   country: this.address.country
      // })
    },

    isFieldsValid() {
      return this._isRequiredFieldsFilled && this.isFieldsPassedValidation;
    },

    addressString() {
      const { unit, street, state, country, zip, city } = this.address;
      return `${street}, ${unit &&
        `${unit},`} ${city}, ${state}, ${zip}, ${country}`;
    }
  },

  methods: {
    _convertListToSelectFormat(key) {
      const arr =
        key === "countries"
          ? COUNTRIES_SELECT_LIST.filter(item => item.key !== "US")
          : STATES_SELECT_LIST;

      let sorted = sortBy(arr, "key");

      if (key === "countries") {
        sorted = [
          { label: "United States of America", key: "US", value: "US" },
          ...sorted
        ];
      }

      return sorted.map(item => ({
        ...item,
        label: item.key
      }));
    },

    _save() {
      this.$emit("save", this.address);
      this.toggleActive();
      this.closeList();
    },

    _cancel() {
      this.address = {
        ...this.initialValues
      };

      this.toggleActive();
      this.closeList();
    },

    updateField(key, value) {
      this.address[key] = value;

      this.closeList();
    },

    toggleActive() {
      this.isActive = !this.isActive;
    },

    closeList() {
      this.countriesDpOpened = false;
      this.statesDpOpened = false;
    }
  },

  watch: {
    initialValues(values) {
      this.address = { ...values };
    }
  }
});
</script>

<style lang="scss" scoped>
$ns: "v-address-editor";

.#{$ns} {
  @include pulsate();

  &.active {
    margin: 25px 0;
  }

  .placeholder-wrapper {
    display: flex;
    margin-bottom: 10px;

    .title {
      color: $default-color;
      white-space: nowrap;
      margin-right: 5px;
      width: 72px;
      min-width: 72px;

      /*@media screen and (min-width: 991px) {*/
      /*margin-right: 5px;*/
      /*width: auto;*/
      /*min-width: auto;*/
      /*}*/
    }

    .value {
      border: 1px solid transparent;
      cursor: pointer;
      border-radius: 2px;
      width: fit-content;
      padding: 3px;
      color: $secondary-text-color;
      font-size: 14px;
      font-family: "Lato-Regular", Arial, sans-serif;
      display: flex;
      align-items: center;
      flex-wrap: wrap;

      &:hover,
      &.active {
        border-color: #b6b6ff;
        box-shadow: 0 0 4px rgba(87, 87, 245, 0.14);
      }

      .address-string {
        position: relative;
        display: inline-block;
        line-height: 19px;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        text-overflow: ellipsis;
        overflow: hidden;
      }
    }

    &.no-state {
      height: 25px;

      span {
        color: $default-color;
      }
    }

    .icon {
      margin-right: 8px;
    }

    .icon,
    .title {
      padding-top: 5px;
    }
  }

  .address-title {
    display: flex;
    font-weight: 700;
    font-size: 14px;
    margin: 15px 0;

    .title {
      padding-left: 8px;
      color: $default-color;
      &.checked {
        color: $success-color;
      }
    }
  }

  &__buttons-wrapper {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    margin-right: 10px;
    max-width: 300px;

    button {
      font-family: Lato-Regular, Arial, sans-serif;
      font-size: 14px;
      margin-right: 10px;
      outline: none;
      color: $default-color;
      transition: color 0.15s linear, border-color 0.15s linear;
      cursor: pointer;
      border: 1px solid transparent;
      border-radius: 4px;
      background: transparent;

      &:last-child {
        margin-right: 0;
      }

      &.save,
      &:hover {
        color: $primary-color;
      }

      &.save:hover {
        animation-play-state: paused;
        border-color: $primary-color;
      }

      &.save.disabled {
        color:gray;
        cursor: default;
        pointer-events: none;

      }
    }
  }
}
</style>

<style lang="scss">
.v-editable-text__wrapper.address-line {
  margin-bottom: 13px;

  .icon {
    align-self: flex-start;
    margin-top: 4px;
  }

  .title {
    /* white-space: pre-wrap !important; */
    white-space: normal;
    line-height: 1.3;
    max-width: 53px;
  }
}
</style>
